/**
 * One section of the report as a card (docs/ux/workspace.md): the heading
 * with its round and open count, the fold toggle (folding never hides open
 * work), the guidance, the matter fields, the question groups in the letter's
 * numbering, the boxes, the signature block and the working notes.
 */

import { cn } from 'cn';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { WorkspaceBox, WorkspaceSection, WorkspaceView } from '@/app/types';
import { StateChip } from '@/components/matter/StateChip';
import { Button } from '@/components/ui/button';
import { longDate, pluralise } from '@/lib/format';
import { foldLabel, humaniseKey, isIsoDate, questionNumberOf, sectionMeta, type Mode } from './describe';
import { groupFoldKey, sectionFoldKey, type Folding } from './folding';
import { ReportBox, type BoxActions } from './ReportBox';
import type { ProofreadingSettings } from './proofreading';
import type { DictationState } from './useWorkspace';

export type BoxContext = {
  role: WorkspaceView['actorRole'];
  mode: Mode;
  matterStatus: number;
  offline: boolean;
  phone: boolean;
  dictation: DictationState;
  dictationMode: 'live' | 'synthetic';
  formattingMode: 'live' | 'synthetic';
  autoFormatting: boolean;
  proofreading: ProofreadingSettings;
  actions: BoxActions;
  busyId: string | null;
  highlighted: string | null;
  readReason: string | null;
  promoted: Set<string>;
  onDismissPromoted: (boxId: string) => void;
  focusTokens: Record<string, number>;
};

function badges(boxes: WorkspaceBox[], folded: boolean): ReactNode {
  const open = boxes.reduce((n, b) => n + b.suggestions.length, 0);
  const questions = boxes.reduce((n, b) => n + b.clarifications.length, 0);
  const empty = boxes.filter((b) => b.editable && b.text.trim() === '').length;
  return (
    <>
      {open > 0 ? <StateChip label={`${open} open`} tone="warning" size="sm" /> : null}
      {questions > 0 ? <StateChip label={pluralise(questions, 'question')} tone="warning" size="sm" /> : null}
      {folded && empty > 0 ? <StateChip label={`${empty} unanswered`} tone="neutral" size="sm" /> : null}
    </>
  );
}

/**
 * `nested` carries a group's children (the schedule's categories) as cards
 * inside this card, so the template's grouping is what the doctor sees; a
 * child is rendered at `level` 3 with its own id, fold and notes.
 */
export function SectionCard({ section, view, folding, ctx, notes, nested, level = 2 }: { section: WorkspaceSection; view: WorkspaceView; folding: Folding; ctx: BoxContext; notes: ReactNode; nested?: ReactNode[]; level?: 2 | 3 }) {
  const folded = folding.isFolded(sectionFoldKey(section.key));
  const meta = sectionMeta(section);
  const editableCount = section.boxes.filter((b) => b.editable).length;
  const canDecideAll = ctx.role === 'doctor' && ctx.mode === 'edit' && section.openSuggestions > 0;
  const nestedCount = nested?.length ?? 0;
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <section id={`section-${section.key}`} aria-labelledby={`section-title-${section.key}`} data-slot="section-card" data-section-key={section.key} data-level={level} className={cn('flex flex-col gap-4 border border-beam-line bg-beam-paper', level === 3 ? 'rounded-lg border-l-2 border-l-beam-line' : 'rounded-xl', ctx.phone ? 'p-4' : level === 3 ? 'p-5' : 'p-6')}>
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <Heading id={`section-title-${section.key}`} className={cn('eyebrow', level === 3 ? 'text-beam-text-muted' : 'text-beam-text-soft')}>{section.label}</Heading>
          {meta.length > 0 ? <p className="text-[13px] text-beam-text-muted">{meta.join(' . ')}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canDecideAll ? (
            <>
              <Button variant="outline" size="sm" className={ctx.phone ? 'h-11' : 'h-8'} disabled={ctx.offline} onClick={() => ctx.actions.decideAll(section.key, 'accept')}>Accept all {section.openSuggestions}</Button>
              <Button variant="outline" size="sm" className={ctx.phone ? 'h-11' : 'h-8'} disabled={ctx.offline} onClick={() => ctx.actions.decideAll(section.key, 'reject')}>Reject all {section.openSuggestions}</Button>
            </>
          ) : null}
          <Button variant="ghost" size="sm" className={ctx.phone ? 'h-11' : 'h-8'} aria-expanded={!folded} aria-controls={`section-body-${section.key}`} aria-label={folded ? `Expand ${section.label}` : `Fold ${section.label} away`} onClick={() => folding.toggle(sectionFoldKey(section.key))}>
            {folded ? <ChevronRightIcon aria-hidden="true" className="size-4" /> : <ChevronDownIcon aria-hidden="true" className="size-4" />}{folded && nestedCount > 0 ? `Show ${pluralise(nestedCount, 'list')}` : foldLabel(section, folded)}
          </Button>
        </div>
      </header>
      {folded ? (
        <p className="flex flex-wrap items-center gap-2 text-[13px] text-beam-text-muted" data-slot="folded-summary">
          {section.kind === 'fields' ? 'Case fields, folded away.' : nestedCount > 0 ? `${pluralise(nestedCount, 'list')}, folded away.` : `${pluralise(editableCount, 'answer box', 'answer boxes')}, folded away.`}
          {badges(section.boxes, true)}
        </p>
      ) : (
        <div id={`section-body-${section.key}`} className="flex flex-col gap-5">
          {section.guidance ? <p className="border-l-2 border-beam-line pl-3 text-[13px] text-beam-text-muted">{section.guidance}</p> : null}
          {section.kind === 'fields' ? <MatterFields fields={view.fields} phone={ctx.phone} /> : null}
          {section.key === 'signature_block' ? <SignatureBlock view={view} /> : null}
          {section.kind === 'questions' ? (
            section.boxes.length === 0 ? <p className="text-[13px] text-beam-text-muted">No questions yet. The letter's questions arrive here when the LOI is approved.</p> : <QuestionGroups section={section} folding={folding} ctx={ctx} />
          ) : section.key !== 'signature_block' && section.kind !== 'fields' ? (
            section.boxes.map((b) => <Box key={b.id} box={b} ctx={ctx} />)
          ) : null}
          {section.headings.filter((h) => h.beforeBoxId === '').map((h) => (
            <p key={h.number} className="text-[15px]"><span className="tabular mr-2 font-semibold">{h.number}</span>{h.text} <span className="text-beam-text-muted">(no answer boxes were extracted for this question)</span></p>
          ))}
          {nestedCount > 0 ? <div data-slot="section-children" className="flex flex-col gap-4">{nested}</div> : null}
        </div>
      )}
      {notes}
    </section>
  );
}

function Box({ box, ctx }: { box: WorkspaceBox; ctx: BoxContext }) {
  return (
    <ReportBox
      box={box} role={ctx.role} mode={ctx.mode} matterStatus={ctx.matterStatus} offline={ctx.offline} phone={ctx.phone}
      dictation={ctx.dictation} dictationMode={ctx.dictationMode} formattingMode={ctx.formattingMode} autoFormatting={ctx.autoFormatting} proofreading={ctx.proofreading} actions={ctx.actions} busyId={ctx.busyId}
      highlighted={ctx.highlighted === box.id} readReason={ctx.readReason} promoted={ctx.promoted.has(box.id)} onDismissPromoted={() => ctx.onDismissPromoted(box.id)}
      focusToken={ctx.focusTokens[box.id] ?? 0}
    />
  );
}

export function QuestionGroups({ section, folding, ctx }: { section: WorkspaceSection; folding: Folding; ctx: BoxContext }) {
  const groups = new Map<string, WorkspaceBox[]>();
  for (const b of section.boxes) {
    const n = questionNumberOf(b);
    groups.set(n, [...(groups.get(n) ?? []), b]);
  }
  return (
    <div className="flex flex-col gap-5">
      {[...groups.entries()].map(([number, boxes]) => {
        const heading = section.headings.find((h) => h.number === number);
        const key = groupFoldKey(section.key, number);
        const folded = folding.isFolded(key);
        const single = boxes.length === 1 && !heading;
        return (
          <div key={number} id={`group-${section.key}-${number}`} data-slot="question-group" className={cn('flex flex-col gap-3', !single && 'border-l-2 border-beam-line pl-4')}>
            {!single ? (
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" aria-expanded={!folded} className={cn('flex min-w-0 flex-1 items-baseline gap-3 rounded-md text-left hover:bg-beam-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature', ctx.phone ? 'min-h-11' : 'min-h-8')} onClick={() => folding.toggle(key)}>
                  <span className="tabular shrink-0 text-[15px] font-semibold">{number}</span>
                  <span className="text-[15px]">{heading?.text ?? `Question ${number}`}</span>
                  <span className="ml-auto text-[13px] text-beam-text-muted">{folded ? `Show ${pluralise(boxes.length, 'part')}` : 'Fold'}</span>
                </button>
                {badges(boxes, folded)}
              </div>
            ) : null}
            {heading && !folded ? <p className="text-[13px] text-beam-text-muted">Heading; parts {boxes.map((b) => b.label.slice(number.length)).join(', ')} are answered.</p> : null}
            {!folded ? boxes.map((b) => <Box key={b.id} box={b} ctx={ctx} />) : null}
          </div>
        );
      })}
    </div>
  );
}

export function MatterFields({ fields, phone }: { fields: Record<string, string>; phone: boolean }) {
  const [open, setOpen] = useState(!phone);
  const entries = Object.entries(fields);
  if (phone && !open) return <Button variant="outline" size="sm" className="h-11 self-start" onClick={() => setOpen(true)}>Show {pluralise(entries.length, 'field')}</Button>;
  return (
    <dl data-slot="matter-fields" className={cn('grid gap-x-6 gap-y-2', phone ? 'grid-cols-1' : 'grid-cols-2')}>
      {entries.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <dt className="text-xs text-beam-text-muted">{humaniseKey(k)}</dt>
          <dd className="text-sm">{isIsoDate(v) ? longDate(v) : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SignatureBlock({ view }: { view: WorkspaceView }) {
  const lines = [
    view.doctor.signatureName,
    view.doctor.credentials,
    view.doctor.hasSignature ? '[Signature applied from the doctor\'s own template. This system does not sign.]' : '[No signature on file. The document can be produced but not completed.]',
    view.finalisedAt ? `Date: ${longDate(view.finalisedAt)}` : 'Date: to be set when the report is finalised',
  ].filter(Boolean);
  return (
    <div data-slot="signature-block" className="flex flex-col gap-1 text-sm">
      {lines.map((l, i) => <p key={i} className={i === 0 ? 'font-semibold' : 'text-beam-text-soft'}>{l}</p>)}
    </div>
  );
}
